import React, { useState, useEffect, useMemo } from 'react';
import { useProfiles } from '../../hooks/useProfiles';
import { useAuth } from '../../hooks/useAuth';
import ProfileCard from './ProfileCard';
import { 
  Container, 
  Typography, 
  Box, 
  CircularProgress, 
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  useMediaQuery,
  Menu,
  MenuItem,
  Slider,
  Divider
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppBadIcon from '@mui/icons-material/GppBad';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import MenuIcon from '@mui/icons-material/Menu';
import { collection, query, where, or, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  Diversity3,  // For "All Ethnicities"
  Person,      // For individual ethnicity options
  Public,      // For "Other"
  Height
} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person'; // For age icon

const ethnicityMap: { [key: string]: string } = {
  "black": "Black",
  "eastAsian": "East Asian",
  "white": "White",
  "middleEastern": "Middle Eastern",
  "southAsian": "South Asian",
  "latinAmerican": "Hispanic/Latino",
  "indigenous": "Indigenous",
  "other": "Other"
};

const colorMap: { [key: string]: string } = {
  "black": "rgb(0, 0, 0)",
  "eastAsian": "rgb(218, 204, 14)",
  "white": "rgb(233, 226, 214)",
  "middleEastern": "rgb(170, 115, 19)",
  "southAsian": "rgb(121, 66, 52)",
  "latinAmerican": "rgb(218, 174, 148)",
  "indigenous": "rgb(216, 100, 38)",
  "other": "rgb(177, 31, 140)"
};

const PROFILE_INDEX_KEY = 'profileListIndex';
type VerificationFilter = 'all' | 'verified' | 'unverified';
type EthnicityFilter = string[];

const ProfileList: React.FC = () => {
  const { profiles, loading, error, sendMatchRequest } = useProfiles();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [currentIndex, setCurrentIndex] = useState(() => {
    // Initialize from localStorage if available
    const savedIndex = localStorage.getItem(PROFILE_INDEX_KEY);
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('all');
  const [ethnicityFilter, setEthnicityFilter] = useState<EthnicityFilter>([]);
  const [sentRequests, setSentRequests] = useState<string[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [heightRange, setHeightRange] = useState<[number, number]>([50, 78]); // 5'0" to 6'6"
  const [ageRange, setAgeRange] = useState<[number, number]>([18, 80]); // Default age range
  const [isLoading, setIsLoading] = useState(true);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Filter and randomize profiles
  const availableProfiles = useMemo(() => {
    const filteredProfiles = profiles.filter(profile => {
      const basicFilter = 
        profile.uid !== user?.uid && 
        !sentRequests.includes(profile.uid) &&
        !receivedRequests.includes(profile.uid);

      const verificationMatch = 
        verificationFilter === 'all' ? true :
        verificationFilter === 'verified' ? profile.verificationStatus === 'verified' :
        profile.verificationStatus !== 'verified';

      const ethnicityMatch = 
        ethnicityFilter.length === 0 ? true :
        profile.ethnicities?.some(e => ethnicityFilter.includes(e));

      // Updated gender matching logic for mutual preference
      const mutualGenderMatch = 
        // Check if the current user matches the profile's preference
        (profile.genderPreference === 'everyone' || 
        (profile.genderPreference === 'men' && user?.gender === 'male') ||
        (profile.genderPreference === 'women' && user?.gender === 'female') ||
        (profile.genderPreference === 'nonbinary' && user?.gender === 'other')) &&
        // Check if the profile matches the user's preference
        (!user?.genderPreference || 
        user.genderPreference === 'everyone' || 
        (user.genderPreference === 'men' && profile.gender === 'male') ||
        (user.genderPreference === 'women' && profile.gender === 'female') ||
        (user.genderPreference === 'nonbinary' && profile.gender === 'other'));

      const heightMatch = 
        profile.height && 
        Number(profile.height) >= heightRange[0] && 
        Number(profile.height) <= heightRange[1];

      const ageMatch = 
        profile.age && 
        Number(profile.age) >= ageRange[0] && 
        Number(profile.age) <= ageRange[1];

      return basicFilter && verificationMatch && ethnicityMatch && mutualGenderMatch && heightMatch && ageMatch;
    });

    return shuffleArray(filteredProfiles);
  }, [profiles, user?.uid, user?.gender, user?.genderPreference, sentRequests, receivedRequests, 
      verificationFilter, ethnicityFilter, heightRange, ageRange]);

  // Reset index when filter changes or when profiles are reshuffled
  useEffect(() => {
    setCurrentIndex(0);
    localStorage.setItem(PROFILE_INDEX_KEY, '0');
  }, [verificationFilter, ethnicityFilter]);

  // Update localStorage when currentIndex changes
  useEffect(() => {
    localStorage.setItem(PROFILE_INDEX_KEY, currentIndex.toString());
  }, [currentIndex]);

  // Reset index if it's out of bounds
  useEffect(() => {
    if (availableProfiles.length > 0 && currentIndex >= availableProfiles.length) {
      setCurrentIndex(0);
    }
  }, [availableProfiles.length, currentIndex]);

  const handleNext = () => {
    setCurrentIndex(prev => 
      prev === availableProfiles.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'matchRequests'),
      or(
        where('senderId', '==', user.uid),
        where('receiverId', '==', user.uid)
      )
    );

    return onSnapshot(q, (snapshot) => {
      const sent: string[] = [];
      const received: string[] = [];
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.senderId === user.uid) {
          sent.push(data.receiverId);
        } else {
          received.push(data.senderId);
        }
      });

      setSentRequests(sent);
      setReceivedRequests(received);
    });
  }, [user]);

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: VerificationFilter,
  ) => {
    if (newFilter !== null) {
      setVerificationFilter(newFilter);
      // Index will be reset by the useEffect above
    }
  };

  const handleEthnicityFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: EthnicityFilter,
  ) => {
    if (newFilter !== null) {
      setEthnicityFilter(newFilter);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) {
        setIsLoading(false);
      }
    }, 1000); // 1 second delay

    if (loading) {
      setIsLoading(true);
    }

    return () => clearTimeout(timer);
  }, [loading]);

  if (isLoading) return (
    <Box 
      display="flex" 
      flexDirection="column"
      justifyContent="center" 
      alignItems="center" 
      height="70vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" color="text.secondary">
        Finding matches for you...
      </Typography>
    </Box>
  );
  
  if (error) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
      <Typography color="error">Error: {error}</Typography>
    </Box>
  );

  const currentProfile = availableProfiles[currentIndex];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Filter Controls - Always visible */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: 2,
        mb: 4,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
        py: 2,
        width: '100%',

      }}>
        <ToggleButtonGroup
          value={verificationFilter}
          exclusive
          onChange={handleFilterChange}
          aria-label="verification filter"
          size={isMobile ? "small" : "medium"}
          sx={isMobile ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            width: '100%',
            '& .MuiToggleButton-root': {
              fontSize: '0.75rem',
              padding: '4px',
              minHeight: '48px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }
          }: {}}
        >
          <ToggleButton 
            value="all" 
            aria-label="show all profiles"
            sx={{ 
              px: 3,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }
            }}
          >
            <AllInclusiveIcon sx={{ mr: 1, color:'rgb(58, 105, 213)' }} />
            All Profiles
          </ToggleButton>
          <ToggleButton 
            value="verified" 
            aria-label="show verified profiles only"
            sx={{ 
              px: 3,
              '&.Mui-selected': {
                bgcolor: 'info.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'info.dark',
                }
              }
            }}
          >
            <VerifiedIcon sx={{ mr: 1 , color:'rgb(58, 213, 60)' }} />
            US Citizens
          </ToggleButton>
          <ToggleButton 
            value="unverified" 
            aria-label="show unverified profiles only"
            sx={{ 
              px: 3,
              '&.Mui-selected': {
                bgcolor: 'warning.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'warning.dark',
                }
              }
            }}
          >
            <GppBadIcon sx={{ mr: 1}} />
            Non US Citizens
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          onClick={handleClick}
          variant="outlined"
          startIcon={<MenuIcon />}
          fullWidth
          sx={{ height: '48px' }}
        >
          Filters
        </Button>
        
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'filter-button',
          }}
          PaperProps={{
            sx: { width: '300px', p: 2 }
          }}
        >
          <Typography gutterBottom>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Age Range
          </Typography>
          <Box sx={{ px: 2, mb: 2 }}>
            <Slider
              value={ageRange}
              onChange={(_, newValue) => setAgeRange(newValue as [number, number])}
              min={18}
              max={80}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              {`${ageRange[0]} to ${ageRange[1]} years old`}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography gutterBottom>
            <Height sx={{ mr: 1, verticalAlign: 'middle' }} />
            Height
          </Typography>
          <Box sx={{ px: 2, mb: 2 }}>
            <Slider
              value={heightRange}
              onChange={(_, newValue) => setHeightRange(newValue as [number, number])}
              min={50}
              max={78}
              valueLabelFormat={(value) => `${Math.floor(value/12)}'${value%12}"`}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              {`${Math.floor(heightRange[0]/12)}'${heightRange[0]%12}" to ${Math.floor(heightRange[1]/12)}'${heightRange[1]%12}"`}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <MenuItem onClick={() => {
            setEthnicityFilter([]);
          }}>
            <Diversity3 sx={{ mr: 1, color: 'blue' }} />
            All Ethnicities
          </MenuItem>
          {Object.entries(ethnicityMap).map(([key, label]) => (
            <MenuItem 
              key={key} 
              onClick={() => {
                setEthnicityFilter(prev => 
                  prev.includes(key) 
                    ? prev.filter(e => e !== key) 
                    : [...prev, key]
                );
              }}
              sx={{
                bgcolor: ethnicityFilter.includes(key) ? 'action.selected' : 'inherit'
              }}
            >
              <Person sx={{ mr: 1, color: colorMap[key] }} />
              {label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
          <Typography color="error">Error: {error}</Typography>
        </Box>
      )}

      {/* No Profiles State */}
      {!loading && !error && availableProfiles.length === 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="70vh" gap={2} sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No profiles match your current filters
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => {
              setVerificationFilter('all');
              setEthnicityFilter([]);
            }}
          >
            Reset Filters
          </Button>
        </Box>
      )}

      {/* Profile Display */}
      {!loading && !error && availableProfiles.length > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            minHeight: '70vh'
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 600 }}>
            <ProfileCard
              profile={currentProfile}
              onMatchRequest={(profileId, message) => {
                if (!user) return;
                sendMatchRequest(user.uid, profileId, message, user.matches);
                handleNext();
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 2 
            }}>
              <IconButton 
                onClick={handleNext}
                sx={{ 
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'background.paper' },
                  padding: '8px 16px',
                  borderRadius: '20px',
                  gap: 1
                }}
              >
                <Typography variant="button" sx={{ color: 'primary.main' }}>Not Interested</Typography>
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ProfileList;